import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Timeline, DataSet } from 'vis-timeline/standalone'
import toast from 'react-hot-toast'
import {
  FaTimes,
  FaCalendarAlt,
  FaFilter,
  FaHeartbeat,
  FaExpand,
  FaSave,
  FaClock
} from 'react-icons/fa'
import { FaMagnifyingGlassPlus, FaMagnifyingGlassMinus } from 'react-icons/fa6'
import CustomButton from '../../../shared/CustomButton'
import { useTranslation } from 'react-i18next'
// Static monitoring categories configuration
const MONITORING_CATEGORIES = {
  'Daily Note': [
    { key: 'situation', label: 'Situation', type: 'textarea' },
    { key: 'nursingConcerns', label: 'Nursing Concerns', type: 'textarea' },
    { key: 'todo', label: 'Todo', type: 'textarea' },
    {
      key: 'awaitingProcedures',
      label: 'Awaiting Procedures',
      type: 'textarea'
    }
  ],
  'Daily Plan': [
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'testsOrProcedure', label: 'Tests Or Procedure', type: 'textarea' },
    { key: 'anticDC', label: 'Antic D C', type: 'textarea' }
  ],
  Precaution: [
    {
      key: 'fall',
      label: 'Fall',
      type: 'select',
      options: ['None', 'Low', 'Medium', 'High']
    },
    {
      key: 'bleeding',
      label: 'Bleeding',
      type: 'select',
      options: ['None', 'Low', 'Medium', 'High']
    },
    {
      key: 'seizures',
      label: 'Seizures',
      type: 'select',
      options: ['None', 'Low', 'Medium', 'High']
    },
    {
      key: 'aspiration',
      label: 'Aspiration',
      type: 'select',
      options: ['None', 'Low', 'Medium', 'High']
    },
    {
      key: 'isolation',
      label: 'Isolation',
      type: 'select',
      options: ['None', 'Contact', 'Droplet', 'Airborne']
    }
  ],
  'Skin Or Wound': [
    { key: 'skinAssessment', label: 'Skin Assessment', type: 'textarea' },
    { key: 'dressingChanges', label: 'Dressing Changes', type: 'textarea' },
    { key: 'skinCare', label: 'Skin Care', type: 'textarea' },
    { key: 'braden', label: 'Braden', type: 'number' }
  ],
  Neuro: [
    {
      key: 'confused',
      label: 'Confused',
      type: 'select',
      options: ['No', 'Yes', 'Mild', 'Moderate', 'Severe']
    },
    {
      key: 'forgetful',
      label: 'Forgetful',
      type: 'select',
      options: ['No', 'Yes', 'Mild', 'Moderate', 'Severe']
    },
    { key: 'others', label: 'Others', type: 'textarea' },
    { key: 'aOrOx', label: 'A Or Ox', type: 'text' },
    { key: 'msk', label: 'Msk', type: 'text' },
    { key: 'gait', label: 'Gait', type: 'text' }
  ],
  Respiratory: [
    { key: 'lungSound', label: 'Lung Sound', type: 'text' },
    { key: 'suppO2', label: 'Supp O2', type: 'text' },
    { key: 'trach', label: 'Trach', type: 'text' },
    { key: 'cpapOrBipap', label: 'Cpap Or Bipap', type: 'text' }
  ],
  Cardiac: [
    { key: 'rhythm', label: 'Rhythm', type: 'text' },
    { key: 'pulses', label: 'Pulses', type: 'text' },
    { key: 'edema', label: 'Edema', type: 'text' }
  ],
  'Gi Gu': [
    { key: 'urine', label: 'Urine', type: 'text' },
    { key: 'void', label: 'Void', type: 'text' },
    { key: 'foley', label: 'Foley', type: 'text' },
    { key: 'bowelSound', label: 'Bowel Sound', type: 'text' },
    { key: 'bowelMovement', label: 'Bowel Movement', type: 'text' }
  ],
  Lab: [
    { key: 'pendingLabs', label: 'Pending Labs', type: 'textarea' },
    { key: 'na', label: 'Na', type: 'number' },
    { key: 'cl', label: 'Cl', type: 'number' },
    { key: 'bun', label: 'Bun', type: 'number' },
    { key: 'k', label: 'K', type: 'number' },
    { key: 'co2', label: 'Co2', type: 'number' },
    { key: 'cr', label: 'Cr', type: 'number' },
    { key: 'glucometerFbs', label: 'Glucometer Fbs', type: 'number' },
    { key: 'glucometer1100', label: 'Glucometer 1100', type: 'number' },
    { key: 'wbc', label: 'Wbc', type: 'number' },
    { key: 'hgb', label: 'Hgb', type: 'number' },
    { key: 'hct', label: 'Hct', type: 'number' },
    { key: 'plt', label: 'Plt', type: 'number' },
    { key: 'ca', label: 'Ca', type: 'number' },
    { key: 'pt', label: 'Pt', type: 'number' },
    { key: 'inr', label: 'Inr', type: 'number' },
    { key: 'mg', label: 'Mg', type: 'number' },
    { key: 'gfr', label: 'Gfr', type: 'number' },
    { key: 'phos', label: 'Phos', type: 'number' },
    { key: 'lactic', label: 'Lactic', type: 'number' },
    { key: 'bmp', label: 'Bmp', type: 'number' },
    { key: 'cbc', label: 'Cbc', type: 'number' },
    { key: 'rbc', label: 'Rbc', type: 'number' },
    { key: 'trop', label: 'Trop', type: 'number' },
    { key: 'others', label: 'Others', type: 'textarea' }
  ],
  Iv: [
    { key: 'lines', label: 'Lines', type: 'text' },
    { key: 'ivFluids', label: 'Iv Fluids', type: 'text' },
    { key: 'tubesAndDrains', label: 'Tubes And Drains', type: 'text' }
  ],
  'Vital Sign': [
    { key: 'q', label: 'Q', type: 'text' },
    { key: 't', label: 'T', type: 'number' },
    { key: 'hr', label: 'Hr', type: 'number' },
    { key: 'rr', label: 'Rr', type: 'number' },
    { key: 'weight', label: 'Weight', type: 'number' },
    { key: 'bp', label: 'Bp', type: 'text' },
    { key: 'o2', label: 'O2', type: 'number' },
    { key: 'pain', label: 'Pain', type: 'number' }
  ],
  'Other Report': [
    { key: 'diet', label: 'Diet', type: 'text' },
    { key: 'vteProphylaxis', label: 'Vte Prophylaxis', type: 'text' },
    { key: 'dischargePlan', label: 'Discharge Plan', type: 'textarea' }
  ]
}

const MonitoringTab = ({ patient }) => {
  // State
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    category: ''
  })
  const {t} = useTranslation();

  const [monitoringData, setMonitoringData] = useState(() => {
    // Initialize with comprehensive sample data
    const today = new Date().toISOString().split('T')[0]
    const currentTime = new Date().toISOString()
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0]
    const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0]

    return {
      [today]: {
        'Vital Sign': {
          t: { value: '98.6', timestamp: currentTime },
          hr: { value: '75', timestamp: currentTime },
          rr: { value: '18', timestamp: currentTime },
          bp: { value: '120/80', timestamp: currentTime },
          o2: { value: '98', timestamp: currentTime },
          weight: { value: '70', timestamp: currentTime },
          pain: { value: '2', timestamp: currentTime }
        },
        'Daily Note': {
          situation: {
            value: 'Patient stable, good appetite',
            timestamp: currentTime
          },
          nursingConcerns: {
            value: 'Monitor pain levels',
            timestamp: currentTime
          },
          todo: {
            value: 'Physical therapy session',
            timestamp: currentTime
          },
          awaitingProcedures: {
            value: 'Blood draw scheduled for morning',
            timestamp: currentTime
          }
        }
      },
      [yesterday]: {
        'Vital Sign': {
          t: { value: '98.4', timestamp: currentTime },
          hr: { value: '72', timestamp: currentTime },
          rr: { value: '16', timestamp: currentTime },
          bp: { value: '118/76', timestamp: currentTime },
          o2: { value: '99', timestamp: currentTime },
          weight: { value: '70.2', timestamp: currentTime },
          pain: { value: '1', timestamp: currentTime }
        },
        Lab: {
          na: { value: '140', timestamp: currentTime },
          k: { value: '4.0', timestamp: currentTime },
          cl: { value: '102', timestamp: currentTime },
          bun: { value: '15', timestamp: currentTime },
          cr: { value: '1.0', timestamp: currentTime },
          wbc: { value: '7.5', timestamp: currentTime },
          hgb: { value: '12.5', timestamp: currentTime },
          hct: { value: '38', timestamp: currentTime },
          pendingLabs: { value: 'CBC, BMP scheduled', timestamp: currentTime }
        },
        Precaution: {
          fall: { value: 'Low', timestamp: currentTime },
          bleeding: { value: 'None', timestamp: currentTime },
          seizures: { value: 'None', timestamp: currentTime },
          aspiration: { value: 'None', timestamp: currentTime },
          isolation: { value: 'None', timestamp: currentTime }
        }
      },
      [twoDaysAgo]: {
        'Daily Plan': {
          description: {
            value: 'Continue current treatment plan',
            timestamp: currentTime
          },
          testsOrProcedure: {
            value: 'Blood work scheduled',
            timestamp: currentTime
          },
          anticDC: {
            value: 'Pending physician review',
            timestamp: currentTime
          }
        },
        'Skin Or Wound': {
          skinAssessment: {
            value: 'Intact, no breakdown',
            timestamp: currentTime
          },
          dressingChanges: { value: 'N/A', timestamp: currentTime },
          skinCare: { value: 'Frequent repositioning', timestamp: currentTime },
          braden: { value: '18', timestamp: currentTime }
        },
        Neuro: {
          confused: { value: 'No', timestamp: currentTime },
          forgetful: { value: 'Mild', timestamp: currentTime },
          others: { value: 'Alert and oriented', timestamp: currentTime },
          aOrOx: { value: 'A&O x3', timestamp: currentTime },
          msk: { value: 'Normal', timestamp: currentTime },
          gait: { value: 'Steady with walker', timestamp: currentTime }
        }
      }
    }
  })
  const [showDialog, setShowDialog] = useState(false)
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [dialogForm, setDialogForm] = useState({})
  const [collapsedCategories, setCollapsedCategories] = useState(new Set())

  // Timeline references
  const timelineRef = useRef(null)
  const timelineContainer = useRef(null)

  // Get category colors
  const getCategoryColor = category => {
    const colors = {
      'Daily Note': '#3B82F6', // Blue
      'Daily Plan': '#10B981', // Green
      Precaution: '#EF4444', // Red
      'Skin Or Wound': '#F59E0B', // Amber
      Neuro: '#8B5CF6', // Purple
      Respiratory: '#06B6D4', // Cyan
      Cardiac: '#EC4899', // Pink
      'Gi Gu': '#84CC16', // Lime
      Lab: '#F97316', // Orange
      Iv: '#6366F1', // Indigo
      'Vital Sign': '#14B8A6', // Teal
      'Other Report': '#64748B' // Slate
    }
    return colors[category] || '#6B7280'
  }

  // Filter categories
  const filteredCategories = useMemo(() => {
    if (!filters.category) return Object.keys(MONITORING_CATEGORIES)
    return Object.keys(MONITORING_CATEGORIES).filter(
      cat => cat === filters.category
    )
  }, [filters.category])

  // Handle day click
  const handleDayClick = useCallback(
    (date, category) => {
      setSelectedDate(date)
      setSelectedCategory(category)

      // Initialize form with existing data or empty values
      const existingData = monitoringData[date]?.[category] || {}
      const formData = {}

      MONITORING_CATEGORIES[category].forEach(item => {
        formData[item.key] = existingData[item.key]?.value || ''
      })

      setDialogForm(formData)
      setShowDialog(true)
    },
    [monitoringData]
  )

  // Initialize timeline
  useEffect(() => {
    if (!timelineContainer.current || timelineRef.current) return

    const options = {
      groupOrder: 'id',
      orientation: 'top',
      zoomable: true,
      moveable: true,
      showMajorLabels: true,
      showMinorLabels: true,
      groupHeightMode: 'fitItems',
      format: {
        minorLabels: {
          day: 'DD',
          month: 'DD MMM'
        },
        majorLabels: {
          day: 'MMMM YYYY',
          month: 'YYYY'
        }
      },
      timeAxis: { scale: 'day', step: 1 },
      start: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000), // 15 days ago
      end: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000), // 15 days from now
      autoResize: true,
      stack: false,
      clickToUse: false,
      selectable: false,
      multiselect: false,
      margin: {
        item: {
          horizontal: 1,
          vertical: 1
        },
        axis: 5
      },
      groupEditable: false
    }

    try {
      timelineRef.current = new Timeline(
        timelineContainer.current,
        new DataSet([]),
        new DataSet([]),
        options
      )

      // Add custom styles
      const styleId = 'monitoring-timeline-styles'
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style')
        style.id = styleId
        style.textContent = `
          .vis-timeline {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            font-family: Arial, sans-serif;
          }
          .vis-item {
            border-radius: 4px;
            opacity: 0.9;
            cursor: pointer;
            overflow: hidden;
          }
          .vis-item:hover {
            opacity: 1;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
          .vis-labelset .vis-label {
            border-right: 1px solid #e5e7eb;
            cursor: pointer;
            padding: 4px 8px;
            font-weight: 500;
            border-bottom: 1px solid #f3f4f6;
          }
          .vis-labelset .vis-label:hover {
            background-color: #f3f4f6;
          }
          .vis-labelset .vis-label.category-group {
            background-color: #f8fafc;
            font-weight: 600;
            font-size: 13px;
            border-bottom: 2px solid #e5e7eb;
          }
          .vis-labelset .vis-label.category-group:hover {
            background-color: #f1f5f9;
          }
          .vis-labelset .vis-label.field-group {
            background-color: #fefefe;
            font-weight: 400;
            font-size: 11px;
            color: #4b5563;
            padding-left: 24px;
          }
          .vis-labelset .vis-label.field-group:hover {
            background-color: #f9fafb;
          }
          .vis-time-axis .vis-text {
            color: #374151;
            font-size: 11px;
          }
          .vis-time-axis .vis-grid.vis-major {
            border-left: 1px solid #d1d5db;
          }
          .vis-time-axis .vis-grid.vis-minor {
            border-left: 1px solid #f3f4f6;
          }
          .vis-panel.vis-background {
            cursor: pointer;
          }
          .vis-panel.vis-background:hover {
            background-color: rgba(59, 130, 246, 0.03);
          }
          .vis-item.vis-range {
            border-radius: 3px;
            border-width: 1px;
            min-height: 18px;
            font-size: 10px;
          }
          .vis-item.vis-range:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            transform: translateY(-1px);
            transition: all 0.2s ease;
          }
          .vis-item .vis-item-content {
            padding: 2px 4px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .vis-labelset .vis-label.vis-group-level-0 {
            background-color: #f9fafb;
            font-weight: 600;
          }
          .vis-item .time-indicator {
            font-size: 8px;
            opacity: 0.8;
            margin-top: 2px;
          }
        `
        document.head.appendChild(style)
      }
    } catch (error) {
      console.error('Error creating timeline:', error)
    }

    return () => {
      if (timelineRef.current) {
        try {
          timelineRef.current.destroy()
        } catch (error) {
          console.warn('Error destroying timeline:', error)
        }
        timelineRef.current = null
      }
    }
  }, [])

  // Update timeline data and handle interactions
  useEffect(() => {
    if (!timelineRef.current) return

    try {
      const groups = new DataSet()
      const items = new DataSet()
      let groupId = 0

      // Create hierarchical groups for categories and their fields
      filteredCategories.forEach(category => {
        const isCollapsed = collapsedCategories.has(category)

        // Add main category group (collapsible parent)
        const categoryGroupId = groupId++
        groups.add({
          id: categoryGroupId,
          content: `${isCollapsed ? '▶' : '▼'} ${category}`,
          style: `
            border-left: 4px solid ${getCategoryColor(category)};
            background-color: ${getCategoryColor(category)}15;
            font-weight: bold;
            padding: 8px;
          `,
          className: 'category-group',
          category: category
        })

        // Add subgroup for each field in the category (only if not collapsed)
        if (!isCollapsed) {
          MONITORING_CATEGORIES[category].forEach(field => {
            const fieldGroupId = groupId++
            groups.add({
              id: fieldGroupId,
              content: `&nbsp;&nbsp;&nbsp;&nbsp;${field.label}`,
              style: `
                border-left: 2px solid ${getCategoryColor(category)}80;
                background-color: #f9fafb;
                font-size: 12px;
                padding-left: 20px;
              `,
              className: 'field-group',
              category: category,
              fieldKey: field.key,
              fieldLabel: field.label
            })

            // Add items for this field if data exists
            Object.keys(monitoringData).forEach(date => {
              const categoryData = monitoringData[date][category]
              if (categoryData && categoryData[field.key]) {
                const fieldData = categoryData[field.key]
                const value = fieldData.value
                const timestamp = fieldData.timestamp
                  ? new Date(fieldData.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : ''

                const displayValue =
                  value.length > 20 ? `${value.substring(0, 20)}...` : value

                items.add({
                  id: `${fieldGroupId}-${date}`,
                  group: fieldGroupId,
                  start: new Date(date),
                  end: new Date(new Date(date).getTime() + 24 * 60 * 60 * 1000),
                  content: `
                    <div>${displayValue}</div>
                    ${
                      timestamp
                        ? `<div class="time-indicator" style="font-size: 12px">${timestamp}</div>`
                        : ''
                    }
                  `,
                  style: `
                    background-color: ${getCategoryColor(category)};
                    border-color: ${getCategoryColor(category)};
                    color: white;
                    font-size: 14px;
                    padding: 2px 6px;
                    border-radius: 3px;
                  `,
                  title: `${category} - ${
                    field.label
                  }\nDate: ${date}\nValue: ${value}\n${
                    timestamp ? `Time: ${timestamp}` : ''
                  }`,
                  category: category,
                  fieldKey: field.key,
                  fieldLabel: field.label
                })
              }
            })
          })
        }
      })

      timelineRef.current.setGroups(groups)
      timelineRef.current.setItems(items)

      // Remove existing click handlers
      timelineRef.current.off('click')

      // Add click handler for background and items
      timelineRef.current.on('click', function (properties) {
        if (properties.time) {
          const clickedDate = new Date(properties.time)
            .toISOString()
            .split('T')[0]

          if (properties.what === 'group-label') {
            // Handle group label click - determine if it's a category or field
            const group = timelineRef.current.groupsData.get(properties.group)
            if (group) {
              // Check if it's a category group (contains ▼ or ▶)
              if (group.content.includes('▼') || group.content.includes('▶')) {
                // Toggle category collapse/expand
                const category = group.category
                const newCollapsed = new Set(collapsedCategories)
                if (newCollapsed.has(category)) {
                  newCollapsed.delete(category)
                } else {
                  newCollapsed.add(category)
                }
                setCollapsedCategories(newCollapsed)
              } else {
                // It's a field group
                handleDayClick(clickedDate, group.category)
              }
            }
          } else if (properties.what === 'item') {
            // Handle item click
            const item = timelineRef.current.itemsData.get(properties.item)
            if (item && item.category) {
              const itemDate = new Date(item.start).toISOString().split('T')[0]
              handleDayClick(itemDate, item.category)
            }
          } else if (
            properties.what === 'background' &&
            properties.group !== null &&
            properties.group !== undefined
          ) {
            // Handle background click (empty day cell)
            const group = timelineRef.current.groupsData.get(properties.group)
            if (group && group.category) {
              handleDayClick(clickedDate, group.category)
            }
          }
        }
      })
    } catch (error) {
      console.warn('Error updating timeline data:', error)
    }
  }, [filteredCategories, monitoringData, handleDayClick, collapsedCategories])

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value })
  }

  // Handle form save
  const handleSaveForm = () => {
    const updatedData = { ...monitoringData }

    if (!updatedData[selectedDate]) {
      updatedData[selectedDate] = {}
    }

    if (!updatedData[selectedDate][selectedCategory]) {
      updatedData[selectedDate][selectedCategory] = {}
    }

    // Update each field with value and timestamp
    Object.keys(dialogForm).forEach(key => {
      if (dialogForm[key] !== '') {
        updatedData[selectedDate][selectedCategory][key] = {
          value: dialogForm[key],
          timestamp: new Date().toISOString()
        }
      }
    })

    setMonitoringData(updatedData)
    setShowDialog(false)
    toast.success(`${selectedCategory} data saved for ${selectedDate}`)
  }

  // Handle zoom functions
  const handleZoomIn = () => {
    if (timelineRef.current) {
      try {
        timelineRef.current.zoomIn(0.2)
      } catch (error) {
        console.warn('Error zooming in:', error)
      }
    }
  }

  const handleZoomOut = () => {
    if (timelineRef.current) {
      try {
        timelineRef.current.zoomOut(0.2)
      } catch (error) {
        console.warn('Error zooming out:', error)
      }
    }
  }

  const handleFitToWindow = () => {
    if (timelineRef.current) {
      try {
        timelineRef.current.fit()
      } catch (error) {
        console.warn('Error fitting to window:', error)
      }
    }
  }

  // Render form field
  const renderFormField = item => {
    const value = dialogForm[item.key] || ''

    switch (item.type) {
      case 'textarea':
        return (
          <div className='relative'>
            <textarea
              value={value}
              onChange={e =>
                setDialogForm({ ...dialogForm, [item.key]: e.target.value })
              }
              className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              rows='3'
              placeholder={`Enter ${item.label.toLowerCase()}`}
            />
          </div>
        )
      case 'select':
        return (
          <div className='relative'>
            <select
              value={value}
              onChange={e =>
                setDialogForm({ ...dialogForm, [item.key]: e.target.value })
              }
              className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            >
              <option value=''>Select {item.label}</option>
              {item.options?.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )
      case 'number':
        return (
          <div className='relative'>
            <input
              type='number'
              value={value}
              onChange={e =>
                setDialogForm({ ...dialogForm, [item.key]: e.target.value })
              }
              className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder={`Enter ${item.label.toLowerCase()}`}
            />
          </div>
        )
      default:
        return (
          <div className='relative'>
            <input
              type='text'
              value={value}
              onChange={e =>
                setDialogForm({ ...dialogForm, [item.key]: e.target.value })
              }
              className='w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder={`Enter ${item.label.toLowerCase()}`}
            />
          </div>
        )
    }
  }

  return (
    <div className='h-full flex flex-col'>
      {/* Header */}
      <div className='p-6 pb-4 flex-shrink-0'>
        <div className='flex items-center justify-between mb-6'>
          <h2 className='text-2xl font-bold'>{t("Patient Monitoring")}</h2>
        </div>

        {/* Filters and Controls */}
        <div className='flex items-center space-x-4 mb-6 flex-wrap'>
          {/* Date Filter */}
          <div className='flex items-center space-x-2'>
            <FaCalendarAlt className='text-gray-500' />
            <label className='text-sm font-medium text-gray-700'>{t("Date")}:</label>
            <input
              type='date'
              value={filters.date}
              onChange={e => handleFilterChange('date', e.target.value)}
              className='border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          {/* Category Filter */}
          <div className='flex items-center space-x-2'>
            <FaFilter className='text-gray-500' />
            <label className='text-sm font-medium text-gray-700'>
              {t("Category")}:
            </label>
            <select
              value={filters.category}
              onChange={e => handleFilterChange('category', e.target.value)}
              className='border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            >
              <option value=''>{t("All Categories")}</option>
              {Object.keys(MONITORING_CATEGORIES).map(category => (
                <option key={category} value={category}>
                  {t(category)}
                </option>
              ))}
            </select>
          </div>

          {/* Timeline Controls */}
          <div className='flex items-center space-x-2 ml-auto'>
            <CustomButton
              onClick={() => setCollapsedCategories(new Set())}
              label={t('Expand All')}
              buttonType='reset'
              className='!h-[40px] text-xs'
              title={t('Expand All Categories')}
            />
            <CustomButton
              onClick={() =>
                setCollapsedCategories(
                  new Set(Object.keys(MONITORING_CATEGORIES))
                )
              }
              label={t('Collapse All')}
              buttonType='reset'
              className='!h-[40px] text-xs'
              title={t('Collapse All Categories')}
            />
            <div className='border-l border-gray-300 mx-2 h-6'></div>
            <CustomButton
              onClick={handleZoomIn}
              icon={<FaMagnifyingGlassPlus size={14} />}
              buttonType='reset'
              className='!h-[40px] !w-[40px] !p-0'
              title='Zoom In'
            />
            <CustomButton
              onClick={handleZoomOut}
              icon={<FaMagnifyingGlassMinus size={14} />}
              buttonType='reset'
              className='!h-[40px] !w-[40px] !p-0'
              title='Zoom Out'
            />
            <CustomButton
              onClick={handleFitToWindow}
              icon={<FaExpand size={14} />}
              buttonType='reset'
              className='!h-[40px] !w-[40px] !p-0'
              title='Fit to Window'
            />
          </div>
        </div>
      </div>

      {/* Timeline Container */}
      <div className='flex-1 px-6 pb-6 overflow-hidden'>
        {' '}
        <div className='bg-white rounded-lg border border-gray-200 p-4 h-full flex flex-col'>
          <div className='flex items-center justify-between mb-4 flex-shrink-0'>
            <h3 className='text-lg font-semibold text-gray-900'>
              {t("Monitoring Timeline")}
            </h3>
            <div className='text-sm text-gray-600 flex items-center space-x-4'>
              <span>
                {t("Click ▼/▶ to expand/collapse • Click field row or data cell to enter values")}
              </span>
              <div className='flex items-center space-x-2'>
                <div className='w-3 h-3 bg-blue-500 rounded opacity-60'></div>
                <span className='text-xs'>{t("Data entry")}</span>
              </div>
            </div>
          </div>

          <div
            ref={timelineContainer}
            className='timeline-container overflow-y-auto'
            style={{ height: '100%', maxHeight: 'calc(100vh - 350px)' }}
          />
        </div>
      </div>

      {/* Dialog for entering monitoring data */}
      {showDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-6'>
                <h3 className='text-xl font-semibold text-gray-900 flex items-center'>
                  <FaHeartbeat
                    className='mr-2'
                    style={{ color: getCategoryColor(selectedCategory) }}
                  />
                  {selectedCategory} -{' '}
                  {new Date(selectedDate).toLocaleDateString()}
                </h3>
                <button
                  onClick={() => setShowDialog(false)}
                  className='text-gray-400 hover:text-gray-600'
                >
                  <FaTimes size={20} />
                </button>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {MONITORING_CATEGORIES[selectedCategory]?.map(item => (
                  <div
                    key={item.key}
                    className={item.type === 'textarea' ? 'col-span-full' : ''}
                  >
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      {item.label}
                    </label>
                    {renderFormField(item)}
                  </div>
                ))}
              </div>

              <div className='flex items-center justify-end space-x-3 mt-6 pt-6 border-t'>
                <CustomButton
                  onClick={() => setShowDialog(false)}
                  label={t('Cancel')}
                  buttonType='reset'
                  className='!h-[40px]'
                />
                <CustomButton
                  onClick={handleSaveForm}
                  label={t('Save')}
                  icon={<FaSave size={14} />}
                  buttonType='primary'
                  className='!h-[40px]'
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MonitoringTab
